// hooks and context
import useOnce from '@hooks/use-once';
// api
import {
  downloadImageBlob,
  downloadRemoteImage,
  getDataUrlFromImageBlob,
} from '@modules/image-helpers';
import type { EmailSignaturesActions } from '@reducers/email-signature-reducer';
import { useCallback, useState } from 'react';

import { EmailSignatureFieldTypes } from '../../../consts';
// types
import type {
  EmailSignatureBanner,
  EmailSignatureBannerId,
  EmailSignatureCrop,
  EmailSignatureV2,
} from '../../../types';
// utils
import type EmailSignatureImagesUploader from '../../EmailSignatureImagesUploader';

interface BannerServices {
  localCroppedBannerImageUrl: string | undefined;
  handleCroppedBannerImageChange: (
    imageReference: string,
    crop: EmailSignatureCrop | null,
    bannerId: EmailSignatureBannerId,
  ) => void;
  originalBannerImageUrl: string;
  handleUploadOriginalBannerImageUrl: (imageReference: string | null, bannerId: string) => void;
  displayBannerInViewMode: boolean;
  handleChangeBannerViewMode: (newDisplaySavedBannerImageContainer: boolean) => void;
}

interface BannerServicesProps {
  emailSignature: EmailSignatureV2;
  emailSignatureDispatch: (action: EmailSignaturesActions) => void;
  emailSignatureHasBeenLoaded: boolean;
  handleOnErrorUploading: (fieldType: EmailSignatureFieldTypes) => void;
  isInTransitionState?: boolean;
  isEditingSavedEmailSignature?: boolean;
  uploadEmailSignatureFieldImagesThunk: (
    uploadImage: (emailSignatureImagesUploader: EmailSignatureImagesUploader) => void,
  ) => void;
}

/*
    This component is a context wrapper that provides all the core services for the email signature side editor,
    it should be used with the email signature Side Editor component. This component allows SideEditor component to
    be reused in different editors (currently used in EmailSignatureMainEditor and EmailSignatureOnboardingEditor)
*/

function useBannerServices({
  emailSignature,
  emailSignatureDispatch,
  emailSignatureHasBeenLoaded,
  isEditingSavedEmailSignature,
  handleOnErrorUploading,
  uploadEmailSignatureFieldImagesThunk,
}: BannerServicesProps): BannerServices {
  const [localCroppedBannerImageUrl, setLocalCroppedBannerImageUrl] = useState<string>();
  const [originalBannerImageUrl, setOriginalBannerImageUrl] = useState<string>('');
  const [displayBannerInViewMode, setDisplayBannerInViewMode] = useState<boolean>(false);

  // When the user loads the saved email signature, we need to initialise the banner original image so that
  // we have the image references to work with
  useOnce(
    (preventSecondRun) => {
      async function initialiseBannerImage(banner: EmailSignatureBanner) {
        const bannerOriginalBlob = await downloadRemoteImage(banner.originalImageUrl);
        const originalBannerDataUrl =
          bannerOriginalBlob && (await getDataUrlFromImageBlob(bannerOriginalBlob));
        setOriginalBannerImageUrl(originalBannerDataUrl || '');
        preventSecondRun();
      }

      if (!emailSignatureHasBeenLoaded || !isEditingSavedEmailSignature) {
        return;
      }

      if (emailSignature.banner?.imageUrl) {
        initialiseBannerImage(emailSignature.banner);
      }

      preventSecondRun();
    },
    [emailSignatureHasBeenLoaded, isEditingSavedEmailSignature, emailSignature.banner],
  );

  useOnce(
    (preventSecond) => {
      if (!emailSignatureHasBeenLoaded || !isEditingSavedEmailSignature) {
        return;
      }

      if (emailSignature.banner?.imageUrl) {
        setDisplayBannerInViewMode(true);
      } else {
        setDisplayBannerInViewMode(false);
      }

      preventSecond();
    },
    [emailSignatureHasBeenLoaded, emailSignature.banner, isEditingSavedEmailSignature],
  );

  const handleUpdateLocalBannerImageUrl = useCallback(
    async (imageReference: string, bannerId: EmailSignatureBannerId) => {
      if (!imageReference) return;
      const blob = await downloadImageBlob(imageReference);
      const dataURL = await getDataUrlFromImageBlob(blob);

      if (!dataURL) return;

      uploadEmailSignatureFieldImagesThunk((emailSignatureImagesUploader) =>
        emailSignatureImagesUploader.uploadImage(
          bannerId,
          dataURL,
          EmailSignatureFieldTypes.BANNER,
          (fieldType: EmailSignatureFieldTypes) => {
            handleOnErrorUploading(fieldType);
            emailSignatureDispatch({
              type: 'DELETE_CUSTOM_BANNER',
            });
          },
        ),
      );
    },
    [emailSignatureDispatch, handleOnErrorUploading, uploadEmailSignatureFieldImagesThunk],
  );

  const handleCroppedBannerImageChange = useCallback(
    (imageReference: string, crop: EmailSignatureCrop | null, bannerId: EmailSignatureBannerId) => {
      emailSignatureDispatch({
        type: 'UPDATE_CUSTOM_BANNER_IMAGE_URL',
        value: { imageReference, crop },
      });

      handleUpdateLocalBannerImageUrl(imageReference, bannerId);
      setLocalCroppedBannerImageUrl(imageReference);
    },
    [handleUpdateLocalBannerImageUrl, emailSignatureDispatch],
  );

  const handleUploadOriginalBannerImageUrl = useCallback(
    async (imageReference: string | null, bannerId: string) => {
      setOriginalBannerImageUrl(imageReference || '');

      if (!imageReference) return;
      const blob = await downloadImageBlob(imageReference);
      const imageDataUrl = await getDataUrlFromImageBlob(blob);
      if (!imageDataUrl) return;

      uploadEmailSignatureFieldImagesThunk((emailSignatureImagesUploader) =>
        emailSignatureImagesUploader.uploadImage(
          // @ts-expect-error - TODO: TS expects a first argument of type EmailSignatureFieldIdTypes here instead of string
          bannerId,
          imageDataUrl,
          EmailSignatureFieldTypes.BANNER,
          (fieldType: EmailSignatureFieldTypes) => {
            handleOnErrorUploading(fieldType);
            emailSignatureDispatch({
              type: 'DELETE_CUSTOM_BANNER',
            });
          },
          true,
        ),
      );
    },
    [emailSignatureDispatch, handleOnErrorUploading, uploadEmailSignatureFieldImagesThunk],
  );

  const handleChangeBannerViewMode = useCallback((newDisplaySavedBannerImageContainer: boolean) => {
    setDisplayBannerInViewMode(newDisplaySavedBannerImageContainer);
  }, []);

  return {
    handleChangeBannerViewMode,
    displayBannerInViewMode,
    localCroppedBannerImageUrl,
    handleCroppedBannerImageChange,
    originalBannerImageUrl,
    handleUploadOriginalBannerImageUrl,
  };
}

export { useBannerServices };
